/**
 * Tour Definitions
 *
 * Conventions:
 *  - Max ~5-7 steps per tour
 *  - No emoji in titles — clean, professional language
 *  - First step: placement 'center', no target (intro frame)
 *  - Last step: placement 'center', no target (completion celebration)
 *  - estimatedDuration ≈ 10-12 seconds per step
 *  - Steps with targets must reference real data-tour="..." attributes in the DOM
 */

import type { Tour, UserSegment } from '@/types/product-tour';

// ── Tour definitions ──────────────────────────────────────────────────────────

export const tours: Tour[] = [

  // ───────────────────────────────────────────────────────────────────────────
  // HOMEPAGE — Executive / Admin
  // ───────────────────────────────────────────────────────────────────────────
  {
    id: 'dashboard-executive',
    name: 'Homepage Overview',
    description: 'Learn how to navigate your personalised homepage, read key metrics, and customise your dashboard layout.',
    trigger: 'first_login',
    priority: 100,
    isOptional: true,
    estimatedDuration: 70,
    pages: ['/dashboard'],
    segment: ['admin', 'manager'],
    steps: [
      {
        id: 'dashboard-executive-welcome',
        title: 'Welcome to your Homepage',
        description: 'This is your personalised hub in Access Projects. It shows your portfolio health, key metrics, and project delivery status at a glance. This short tour will walk you through the main areas.',
        placement: 'center',
      },
      {
        id: 'dashboard-executive-greeting',
        title: 'Your personalised greeting',
        description: 'The page header shows a greeting based on the time of day and displays your role-appropriate subtitle — helping you orient quickly each time you log in.',
        target: '[data-tour="dashboard-header"]',
        placement: 'bottom',
        spotlightPadding: 12,
      },
      {
        id: 'dashboard-executive-kpis',
        title: 'Portfolio Pulse — key metrics',
        description: 'These KPI tiles give you an instant read on portfolio-wide performance: total active projects, revenue, gross profit, and utilisation. Trend arrows show movement from the prior period.',
        target: '[data-tour="dashboard-kpi-tiles"]',
        placement: 'bottom',
        spotlightPadding: 10,
      },
      {
        id: 'dashboard-executive-at-risk',
        title: 'Projects requiring attention',
        description: 'This widget surfaces projects flagged as amber or red health — so you can act on risks before they escalate. Click any row to open the full project details.',
        target: '[data-tour="dashboard-at-risk"]',
        placement: 'top',
        spotlightPadding: 10,
      },
      {
        id: 'dashboard-executive-settings',
        title: 'Customise your layout',
        description: 'Use the settings cog to show or hide individual widgets. Your preferences are saved automatically and persist across sessions.',
        target: '[data-tour="dashboard-settings-cog"]',
        placement: 'left',
        spotlightPadding: 12,
      },
      {
        id: 'dashboard-executive-complete',
        title: 'You are ready to go',
        description: 'You now know the key areas of your homepage. Explore the widgets, customise your layout, and use the Help button in the top-right at any time to come back to this guide.',
        placement: 'center',
      },
    ],
  },

  // ───────────────────────────────────────────────────────────────────────────
  // HOMEPAGE — Project Manager
  // ───────────────────────────────────────────────────────────────────────────
  {
    id: 'dashboard-pm',
    name: 'Homepage Overview',
    description: 'Get oriented with your project manager homepage — metrics, delivery health, and your project list.',
    trigger: 'first_login',
    priority: 90,
    isOptional: true,
    estimatedDuration: 60,
    pages: ['/dashboard'],
    segment: ['new_user', 'returning_user', 'employee'],
    steps: [
      {
        id: 'dashboard-pm-welcome',
        title: 'Welcome to your Homepage',
        description: 'This is your personalised hub in Access Projects. It shows project delivery status, key metrics, and your team at a glance. This short tour will walk you through the main areas.',
        placement: 'center',
      },
      {
        id: 'dashboard-pm-greeting',
        title: 'Your personalised greeting',
        description: 'The page header shows a greeting based on the time of day and a subtitle relevant to your role — helping you orient quickly each time you log in.',
        target: '[data-tour="dashboard-header"]',
        placement: 'bottom',
        spotlightPadding: 12,
      },
      {
        id: 'dashboard-pm-kpis',
        title: 'Your key metrics',
        description: 'These tiles show your project portfolio at a glance — active projects, budget health, and utilisation. Trend arrows highlight changes from the previous period.',
        target: '[data-tour="dashboard-kpi-tiles"]',
        placement: 'bottom',
        spotlightPadding: 10,
      },
      {
        id: 'dashboard-pm-settings',
        title: 'Customise your layout',
        description: 'Click the settings cog to show or hide widgets on your homepage. Your preferences are saved automatically.',
        target: '[data-tour="dashboard-settings-cog"]',
        placement: 'left',
        spotlightPadding: 12,
      },
      {
        id: 'dashboard-pm-complete',
        title: 'You are ready to go',
        description: 'You now know the key areas of your homepage. Use the Help button in the top-right at any time to revisit this guide or explore the User Guide and FAQ tabs.',
        placement: 'center',
      },
    ],
  },

  // ───────────────────────────────────────────────────────────────────────────
  // PROJECTS LIST
  // ───────────────────────────────────────────────────────────────────────────
  {
    id: 'projects-list',
    name: 'Projects List Overview',
    description: 'Learn how to search, filter, and manage your project list — including creating new projects and exporting data.',
    trigger: 'first_login',
    priority: 95,
    isOptional: true,
    estimatedDuration: 70,
    pages: ['/projects'],
    steps: [
      {
        id: 'projects-list-welcome',
        title: 'Welcome to the Projects List',
        description: 'This is the central hub for all your projects. You can search, filter, sort, and manage every project from here. This tour will walk you through the key features.',
        placement: 'center',
      },
      {
        id: 'projects-list-header',
        title: 'Page header and actions',
        description: 'The header shows the page title and — if you have permission — a button to create a new project. Minimised project creation forms are also restored here.',
        target: '[data-tour="projects-header"]',
        placement: 'bottom',
        spotlightPadding: 10,
      },
      {
        id: 'projects-list-search',
        title: 'Search and filter bar',
        description: 'Use the search box to find projects by name, code, or client. The Status and Health dropdowns quickly narrow results. The filter icon opens advanced column-level filters.',
        target: '[data-tour="projects-search-bar"]',
        placement: 'bottom',
        spotlightPadding: 10,
      },
      {
        id: 'projects-list-table',
        title: 'The projects table',
        description: 'Each row is a project. Click a row to open project details. You can multi-select rows using checkboxes for bulk actions like status changes, manager reassignment, or deletion.',
        target: '[data-tour="projects-table"]',
        placement: 'top',
        spotlightPadding: 8,
      },
      {
        id: 'projects-list-columns',
        title: 'Column configuration',
        description: 'Use the columns icon to show, hide, and reorder the table columns. Your configuration is saved automatically per user.',
        target: '[data-tour="projects-columns-btn"]',
        placement: 'left',
        spotlightPadding: 12,
      },
      {
        id: 'projects-list-complete',
        title: 'You know the Projects List',
        description: 'You can now search, filter, create, and manage all your projects from this page. Open any project row to dive into full details, budgets, resourcing, and more.',
        placement: 'center',
      },
    ],
  },

  // ───────────────────────────────────────────────────────────────────────────
  // PROJECT DETAILS — one tour per tab, each gated by requiredTab
  // ───────────────────────────────────────────────────────────────────────────

  // Navigation & header (always visible — no requiredTab)
  {
    id: 'project-details-navigation',
    name: 'Project Details — Navigation',
    description: 'Learn how to navigate a project — the header, tab bar, and actions menu.',
    trigger: 'feature_discovery',
    priority: 90,
    isOptional: true,
    estimatedDuration: 50,
    pages: ['/projects/'],
    steps: [
      {
        id: 'pd-nav-welcome',
        title: 'Project Details Page',
        description: 'This page gives you complete visibility of a single project. It is organised into tabs — each covering a different aspect of the project. This short tour covers the header and how to navigate between tabs.',
        placement: 'center',
      },
      {
        id: 'pd-nav-header',
        title: 'Project header',
        description: 'The header shows the project code, name, and description. Click the name or description directly to edit them inline — changes are saved immediately.',
        target: '[data-tour="project-details-header"]',
        placement: 'bottom',
        spotlightPadding: 10,
      },
      {
        id: 'pd-nav-tabs',
        title: 'Tab bar',
        description: 'Use the tabs to move between sections. The tabs shown here depend on your role and which features your administrator has enabled. Use the settings cog at the right of the tab bar to reorder or hide tabs.',
        target: '[data-tour="project-details-tabs"]',
        placement: 'bottom',
        spotlightPadding: 8,
      },
      {
        id: 'pd-nav-actions',
        title: 'Actions menu',
        description: 'The actions menu in the top-right gives access to project-level operations such as editing project details, viewing revision history, and exporting data.',
        target: '[data-tour="project-details-actions"]',
        placement: 'left',
        spotlightPadding: 12,
      },
      {
        id: 'pd-nav-complete',
        title: 'Ready to explore',
        description: 'You now know how to navigate a project. Use the tab-specific tours in Help to get a walkthrough of each section.',
        placement: 'center',
      },
    ],
  },

  // Overview tab
  {
    id: 'project-details-overview',
    name: 'Project Details — Overview Tab',
    description: 'Understand the Overview tab — project health, key dates, the project team, and recent activity.',
    trigger: 'feature_discovery',
    priority: 88,
    isOptional: true,
    estimatedDuration: 50,
    pages: ['/projects/'],
    requiredTab: 'overview',
    steps: [
      {
        id: 'pd-overview-welcome',
        title: 'Overview Tab',
        description: 'The Overview tab is the first place to check for a quick project pulse. It brings together health indicators, key dates, the project team, and recent activity in one place.',
        placement: 'center',
      },
      {
        id: 'pd-overview-content',
        title: 'Project summary',
        description: 'Here you can see the project health score, start and end dates, assigned project manager, client, and a timeline of recent changes. All values update in real time as the project progresses.',
        target: '[data-tour="project-details-overview"]',
        placement: 'top',
        spotlightPadding: 10,
      },
      {
        id: 'pd-overview-complete',
        title: 'Overview covered',
        description: 'Use the Financials tab for budget and revenue details, or the Team tab to review who is assigned to this project.',
        placement: 'center',
      },
    ],
  },

  // Financials tab
  {
    id: 'project-details-financials',
    name: 'Project Details — Financials Tab',
    description: 'Walk through the Financials tab — revenue, cost, margin, and burn-down charts.',
    trigger: 'feature_discovery',
    priority: 86,
    isOptional: true,
    estimatedDuration: 60,
    pages: ['/projects/'],
    requiredTab: 'financials',
    steps: [
      {
        id: 'pd-financials-welcome',
        title: 'Financials Tab',
        description: 'The Financials tab shows a complete financial picture of the project — revenue recognised, costs incurred, gross margin, and projected performance against budget.',
        placement: 'center',
      },
      {
        id: 'pd-financials-content',
        title: 'Performance summary and charts',
        description: 'The top section shows key financial KPIs at a glance. Below, burn-down and margin charts let you track how actuals are tracking against the plan over time.',
        target: '[data-tour="project-details-financials"]',
        placement: 'top',
        spotlightPadding: 10,
      },
      {
        id: 'pd-financials-complete',
        title: 'Financials covered',
        description: 'For a detailed breakdown of how revenue is generated, open the Billing tab. For cost breakdown by category, explore the Cost Budget tab.',
        placement: 'center',
      },
    ],
  },

  // Billing tab
  {
    id: 'project-details-billing',
    name: 'Project Details — Billing Tab',
    description: 'Learn how to manage invoices, drafts, and billing configuration for a project.',
    trigger: 'feature_discovery',
    priority: 84,
    isOptional: true,
    estimatedDuration: 60,
    pages: ['/projects/'],
    requiredTab: 'billing',
    steps: [
      {
        id: 'pd-billing-welcome',
        title: 'Billing Tab',
        description: 'The Billing tab is where you manage all invoicing for the project — creating drafts, reviewing sent invoices, and configuring how the project is billed.',
        placement: 'center',
      },
      {
        id: 'pd-billing-content',
        title: 'Invoices and drafts',
        description: 'The invoice table lists all raised invoices with their status (draft, sent, paid). Use the action buttons to create a new draft, post to financials, or send directly to the client.',
        target: '[data-tour="project-details-billing"]',
        placement: 'top',
        spotlightPadding: 10,
      },
      {
        id: 'pd-billing-complete',
        title: 'Billing covered',
        description: 'All billing activity is automatically recorded in the Audit tab so you have a full history of invoices raised and sent.',
        placement: 'center',
      },
    ],
  },

  // Sales Budget tab
  {
    id: 'project-details-sales-budget',
    name: 'Project Details — Sales Budget Tab',
    description: 'Understand the Sales Budget tab — revenue targets, fee structures, and budget tracking.',
    trigger: 'feature_discovery',
    priority: 82,
    isOptional: true,
    estimatedDuration: 60,
    pages: ['/projects/'],
    requiredTab: 'salesBudget',
    steps: [
      {
        id: 'pd-sales-budget-welcome',
        title: 'Sales Budget Tab',
        description: 'The Sales Budget tab lets you define and track the revenue budget for this project — broken down by fee type, phase, or billing milestone depending on the project type.',
        placement: 'center',
      },
      {
        id: 'pd-sales-budget-content',
        title: 'Revenue budget',
        description: 'Review budgeted versus actual revenue here. You can edit the budget directly from this tab — any changes are versioned and recorded in the audit log.',
        target: '[data-tour="project-details-sales-budget"]',
        placement: 'top',
        spotlightPadding: 10,
      },
      {
        id: 'pd-sales-budget-complete',
        title: 'Sales Budget covered',
        description: 'Pair this with the Cost Budget tab to understand your margin position, or use the Financials tab for the high-level financial summary.',
        placement: 'center',
      },
    ],
  },

  // Cost Budget tab
  {
    id: 'project-details-cost-budget',
    name: 'Project Details — Cost Budget Tab',
    description: 'Review the Cost Budget tab — planned costs, actuals, and variance tracking.',
    trigger: 'feature_discovery',
    priority: 80,
    isOptional: true,
    estimatedDuration: 60,
    pages: ['/projects/'],
    requiredTab: 'costBudget',
    steps: [
      {
        id: 'pd-cost-budget-welcome',
        title: 'Cost Budget Tab',
        description: 'The Cost Budget tab shows all planned expenditure for the project broken down by cost category. It compares budget against actuals and flags variances so you can act early.',
        placement: 'center',
      },
      {
        id: 'pd-cost-budget-content',
        title: 'Cost categories and variances',
        description: 'Each row represents a cost category — labour, materials, subcontractors, and more. The variance column highlights where you are over or under budget.',
        target: '[data-tour="project-details-cost-budget"]',
        placement: 'top',
        spotlightPadding: 10,
      },
      {
        id: 'pd-cost-budget-complete',
        title: 'Cost Budget covered',
        description: 'Use the Resource Budget tab to see the same picture broken down by person, or the Financials tab for an overall financial summary.',
        placement: 'center',
      },
    ],
  },

  // Resource Budget tab
  {
    id: 'project-details-resource-budget',
    name: 'Project Details — Resource Budget Tab',
    description: 'Understand the Resource Budget tab — planned hours and costs per team member.',
    trigger: 'feature_discovery',
    priority: 78,
    isOptional: true,
    estimatedDuration: 60,
    pages: ['/projects/'],
    requiredTab: 'resourceBudget',
    steps: [
      {
        id: 'pd-resource-budget-welcome',
        title: 'Resource Budget Tab',
        description: 'The Resource Budget tab breaks the project budget down by individual team member — showing planned hours, estimated cost, and how actuals are tracking against the plan.',
        placement: 'center',
      },
      {
        id: 'pd-resource-budget-content',
        title: 'Resource-level budgets',
        description: 'Each row represents a team member\'s budget allocation. You can edit planned hours directly and see how changes affect the overall project cost.',
        target: '[data-tour="project-details-resource-budget"]',
        placement: 'top',
        spotlightPadding: 10,
      },
      {
        id: 'pd-resource-budget-complete',
        title: 'Resource Budget covered',
        description: 'For a broader view of resource availability across all projects, visit the Utilisation page from the main navigation.',
        placement: 'center',
      },
    ],
  },

  // Cost Centres tab
  {
    id: 'project-details-cost-centres',
    name: 'Project Details — Cost Centres Tab',
    description: 'Learn how to use Cost Centres to organise and track project expenditure by department or structure.',
    trigger: 'feature_discovery',
    priority: 76,
    isOptional: true,
    estimatedDuration: 60,
    pages: ['/projects/'],
    requiredTab: 'costCentres',
    steps: [
      {
        id: 'pd-cost-centres-welcome',
        title: 'Cost Centres Tab',
        description: 'Cost Centres let you organise project costs into structured groupings — by department, phase, or any custom hierarchy that matches how your business tracks expenditure.',
        placement: 'center',
      },
      {
        id: 'pd-cost-centres-content',
        title: 'Cost centre structure',
        description: 'Each cost centre shows its budget, committed spend, and actual costs. You can drill into a centre to see the individual transactions that make up the total.',
        target: '[data-tour="project-details-cost-centres"]',
        placement: 'top',
        spotlightPadding: 10,
      },
      {
        id: 'pd-cost-centres-complete',
        title: 'Cost Centres covered',
        description: 'Use the Configure Structure button to define your cost centre hierarchy, or the Cost Budget tab for an overall budget breakdown.',
        placement: 'center',
      },
    ],
  },

  // Purchases tab
  {
    id: 'project-details-purchases',
    name: 'Project Details — Purchases Tab',
    description: 'Track purchase orders and commitments against the project budget.',
    trigger: 'feature_discovery',
    priority: 74,
    isOptional: true,
    estimatedDuration: 50,
    pages: ['/projects/'],
    requiredTab: 'purchases',
    steps: [
      {
        id: 'pd-purchases-welcome',
        title: 'Purchases Tab',
        description: 'The Purchases tab lists all purchase orders and committed costs raised against this project. It gives you visibility of spend that is contracted but not yet invoiced.',
        placement: 'center',
      },
      {
        id: 'pd-purchases-content',
        title: 'Purchase orders and commitments',
        description: 'Each row shows a purchase order with its supplier, value, and current status. Committed amounts are factored into the project\'s projected cost-to-complete.',
        target: '[data-tour="project-details-purchases"]',
        placement: 'top',
        spotlightPadding: 10,
      },
      {
        id: 'pd-purchases-complete',
        title: 'Purchases covered',
        description: 'Committed purchase amounts feed directly into the Cost Budget and Financials tabs so your project cost forecasts always reflect contracted spend.',
        placement: 'center',
      },
    ],
  },

  // Time tab
  {
    id: 'project-details-time',
    name: 'Project Details — Time Tab',
    description: 'Review all time entries logged against this project, filtered by team member or date.',
    trigger: 'feature_discovery',
    priority: 72,
    isOptional: true,
    estimatedDuration: 50,
    pages: ['/projects/'],
    requiredTab: 'time',
    steps: [
      {
        id: 'pd-time-welcome',
        title: 'Time Tab',
        description: 'The Time tab shows every time entry logged against this project. You can filter by team member, date range, or billable status to find exactly what you need.',
        placement: 'center',
      },
      {
        id: 'pd-time-content',
        title: 'Time entries',
        description: 'Each row shows who logged time, on which date, for how long, and whether it is billable. Time entries here flow directly into the Financials tab as recognised revenue or cost.',
        target: '[data-tour="project-details-time"]',
        placement: 'top',
        spotlightPadding: 10,
      },
      {
        id: 'pd-time-complete',
        title: 'Time covered',
        description: 'Use the Expenses tab to review non-time costs, or the Resource Budget tab to compare logged hours against each team member\'s planned budget.',
        placement: 'center',
      },
    ],
  },

  // Expenses tab
  {
    id: 'project-details-expenses',
    name: 'Project Details — Expenses Tab',
    description: 'Review all expense claims and out-of-pocket costs recorded against this project.',
    trigger: 'feature_discovery',
    priority: 70,
    isOptional: true,
    estimatedDuration: 50,
    pages: ['/projects/'],
    requiredTab: 'expenses',
    steps: [
      {
        id: 'pd-expenses-welcome',
        title: 'Expenses Tab',
        description: 'The Expenses tab lists all out-of-pocket costs and expense claims submitted against this project — travel, accommodation, materials, and any other ad-hoc costs.',
        placement: 'center',
      },
      {
        id: 'pd-expenses-content',
        title: 'Expense entries',
        description: 'Each expense shows who submitted it, the category, amount, and approval status. Approved expenses are included in the project\'s actual cost calculations.',
        target: '[data-tour="project-details-expenses"]',
        placement: 'top',
        spotlightPadding: 10,
      },
      {
        id: 'pd-expenses-complete',
        title: 'Expenses covered',
        description: 'Expenses feed into the Cost Budget and Financials tabs. Use the Purchases tab for contracted supplier costs or the Time tab for labour costs.',
        placement: 'center',
      },
    ],
  },

  // Team tab
  {
    id: 'project-details-team',
    name: 'Project Details — Team Tab',
    description: 'See who is on the project team, their roles, and how their hours are tracking.',
    trigger: 'feature_discovery',
    priority: 68,
    isOptional: true,
    estimatedDuration: 50,
    pages: ['/projects/'],
    requiredTab: 'team',
    steps: [
      {
        id: 'pd-team-welcome',
        title: 'Team Tab',
        description: 'The Team tab shows everyone assigned to this project — their role, allocated hours, hours logged so far, and utilisation status.',
        placement: 'center',
      },
      {
        id: 'pd-team-content',
        title: 'Team members and utilisation',
        description: 'Each row shows a team member with their allocation status — On Track, At Risk, or Over Allocated. Use this to spot resource pressure early and take action before it affects delivery.',
        target: '[data-tour="project-details-team"]',
        placement: 'top',
        spotlightPadding: 10,
      },
      {
        id: 'pd-team-complete',
        title: 'Team covered',
        description: 'For a broader view of resource availability across all projects, visit the Utilisation page. For detailed scheduling, use the Resourcing tab.',
        placement: 'center',
      },
    ],
  },

  // Resourcing tab
  {
    id: 'project-details-resourcing',
    name: 'Project Details — Resourcing Tab',
    description: 'Manage resource bookings and scheduling for this project.',
    trigger: 'feature_discovery',
    priority: 66,
    isOptional: true,
    estimatedDuration: 60,
    pages: ['/projects/'],
    requiredTab: 'resourcing',
    steps: [
      {
        id: 'pd-resourcing-welcome',
        title: 'Resourcing Tab',
        description: 'The Resourcing tab connects to your scheduling system and shows all resource bookings for this project. It helps you plan who is working on the project and when.',
        placement: 'center',
      },
      {
        id: 'pd-resourcing-content',
        title: 'Resource bookings',
        description: 'Bookings are displayed in a timeline view showing each team member\'s scheduled hours week by week. You can identify gaps or over-allocations at a glance.',
        target: '[data-tour="project-details-resourcing"]',
        placement: 'top',
        spotlightPadding: 10,
      },
      {
        id: 'pd-resourcing-complete',
        title: 'Resourcing covered',
        description: 'Changes made in the scheduling system are reflected here automatically. For unbooked team members flagged at the top of this tab, take action in your scheduler to resolve them.',
        placement: 'center',
      },
    ],
  },

  // Audit tab
  {
    id: 'project-details-audit',
    name: 'Project Details — Audit Tab',
    description: 'Review the full audit trail of changes made to this project.',
    trigger: 'feature_discovery',
    priority: 62,
    isOptional: true,
    estimatedDuration: 40,
    pages: ['/projects/'],
    requiredTab: 'audit',
    steps: [
      {
        id: 'pd-audit-welcome',
        title: 'Audit Tab',
        description: 'The Audit tab provides a complete, tamper-proof record of every change made to this project — who made the change, what was changed, and when.',
        placement: 'center',
      },
      {
        id: 'pd-audit-content',
        title: 'Audit trail',
        description: 'Each entry shows the action taken, the user who performed it, and a timestamp. You can filter by event type or date range to find specific changes quickly.',
        target: '[data-tour="project-details-audit"]',
        placement: 'top',
        spotlightPadding: 10,
      },
      {
        id: 'pd-audit-complete',
        title: 'Audit covered',
        description: 'The audit log captures changes from all tabs — status updates, budget edits, team changes, and invoice activity are all recorded here automatically.',
        placement: 'center',
      },
    ],
  },

  // Reports tab
  {
    id: 'project-details-reports',
    name: 'Project Details — Reports Tab',
    description: 'Access project-level reports — P&L, forecast, and status reports scoped to this project.',
    trigger: 'feature_discovery',
    priority: 60,
    isOptional: true,
    estimatedDuration: 40,
    pages: ['/projects/'],
    requiredTab: 'reports',
    steps: [
      {
        id: 'pd-reports-welcome',
        title: 'Reports Tab',
        description: 'The Reports tab provides project-scoped versions of the key financial and status reports — all filtered to this project so you can review performance without leaving the project view.',
        placement: 'center',
      },
      {
        id: 'pd-reports-content',
        title: 'Project reports',
        description: 'Available reports include the Project P&L, Budget Variance, and Project Forecast. Each report opens in-context and can be exported for sharing with stakeholders.',
        target: '[data-tour="project-details-reports"]',
        placement: 'top',
        spotlightPadding: 10,
      },
      {
        id: 'pd-reports-complete',
        title: 'Reports covered',
        description: 'For cross-project reporting across your whole portfolio, use the Reports section in the main navigation.',
        placement: 'center',
      },
    ],
  },

  // ───────────────────────────────────────────────────────────────────────────
  // PORTFOLIO
  // ───────────────────────────────────────────────────────────────────────────
  {
    id: 'portfolio-overview',
    name: 'Portfolio Overview Tour',
    description: 'Understand the portfolio view — summary metrics, health heat map, and at-a-glance project performance.',
    trigger: 'first_login',
    priority: 80,
    isOptional: true,
    estimatedDuration: 60,
    pages: ['/portfolio'],
    segment: ['admin', 'manager'],
    steps: [
      {
        id: 'portfolio-welcome',
        title: 'Portfolio Overview',
        description: 'The Portfolio page gives you an executive-level view across all active projects. This tour will walk you through the summary cards, health heat map, and the project list.',
        placement: 'center',
      },
      {
        id: 'portfolio-summary-cards',
        title: 'Portfolio summary metrics',
        description: 'The four summary cards show total projects, portfolio value, average gross margin, and total WIP (unbilled work). These update automatically as project data changes.',
        target: '[data-tour="portfolio-summary-cards"]',
        placement: 'bottom',
        spotlightPadding: 10,
      },
      {
        id: 'portfolio-health-map',
        title: 'Project health heat map',
        description: 'Each card in the heat map represents one active project, colour-coded by health: green is healthy, amber is at risk, and red is critical. Click any card to open the full project details.',
        target: '[data-tour="portfolio-health-map"]',
        placement: 'top',
        spotlightPadding: 10,
      },
      {
        id: 'portfolio-project-list',
        title: 'Detailed project breakdown',
        description: 'The table below the heat map lists all active projects with completion percentage, budget position, and key dates. Use it to identify projects that need immediate attention.',
        target: '[data-tour="portfolio-project-list"]',
        placement: 'top',
        spotlightPadding: 8,
      },
      {
        id: 'portfolio-complete',
        title: 'Portfolio view ready',
        description: 'You now know how to read the Portfolio Overview. Use this page for board-level reporting and quick triage. Drill into individual projects from the heat map or project list.',
        placement: 'center',
      },
    ],
  },

  // ───────────────────────────────────────────────────────────────────────────
  // UTILISATION
  // ───────────────────────────────────────────────────────────────────────────
  {
    id: 'utilisation-overview',
    name: 'Team Utilisation Tour',
    description: 'Learn how to read utilisation metrics, identify over- and under-allocated resources, and act on capacity issues.',
    trigger: 'first_login',
    priority: 75,
    isOptional: true,
    estimatedDuration: 60,
    pages: ['/utilisation'],
    segment: ['admin', 'manager'],
    steps: [
      {
        id: 'utilisation-welcome',
        title: 'Team Utilisation',
        description: 'This page shows billable hour performance across your team. You can quickly spot who is over-allocated, under-utilised, or on target — and take action before it impacts delivery.',
        placement: 'center',
      },
      {
        id: 'utilisation-summary-cards',
        title: 'Utilisation summary',
        description: 'The four summary cards show average team utilisation, the number of people on target, under-utilised, and over-allocated. The progress bar gives a visual sense of overall team capacity.',
        target: '[data-tour="utilisation-summary-cards"]',
        placement: 'bottom',
        spotlightPadding: 10,
      },
      {
        id: 'utilisation-table',
        title: 'Individual utilisation breakdown',
        description: 'The table lists each team member\'s billable hours, available hours, utilisation percentage, target, and current status. Sort by any column to find the most pressing capacity issues.',
        target: '[data-tour="utilisation-table"]',
        placement: 'top',
        spotlightPadding: 8,
      },
      {
        id: 'utilisation-complete',
        title: 'Utilisation view ready',
        description: 'Use this page each week to review your team\'s capacity. Green means on target, amber means under-utilised, and red means over-allocated. Act early to protect delivery quality.',
        placement: 'center',
      },
    ],
  },

  // ───────────────────────────────────────────────────────────────────────────
  // RESOURCE DIRECTORY
  // ───────────────────────────────────────────────────────────────────────────
  {
    id: 'directory-overview',
    name: 'Resource Directory Tour',
    description: 'Learn how to browse and search your team directory, view employee profiles, and manage skills and project allocations.',
    trigger: 'first_login',
    priority: 70,
    isOptional: true,
    estimatedDuration: 70,
    pages: ['/directory'],
    steps: [
      {
        id: 'directory-welcome',
        title: 'Resource Directory',
        description: 'The Directory is your central people database. It shows every employee with their role, department, skills, and project allocations. This tour covers the key features.',
        placement: 'center',
      },
      {
        id: 'directory-search',
        title: 'Search and filter',
        description: 'Use the search box to find anyone by name, role, or department. The filter panel lets you narrow by department, skill, employment type, or status.',
        target: '[data-tour="directory-search"]',
        placement: 'bottom',
        spotlightPadding: 10,
      },
      {
        id: 'directory-view-toggle',
        title: 'Grid and list views',
        description: 'Toggle between the card grid — ideal for browsing — and the list view — ideal for comparing columns across multiple employees. The list view also supports custom column configuration.',
        target: '[data-tour="directory-view-toggle"]',
        placement: 'bottom',
        spotlightPadding: 12,
      },
      {
        id: 'directory-employee-cards',
        title: 'Employee cards',
        description: 'Each card shows a photo, name, role, department, and key details. Click any card or row to open the employee profile with full skills, rates, working pattern, and project allocations.',
        target: '[data-tour="directory-employee-grid"]',
        placement: 'top',
        spotlightPadding: 8,
      },
      {
        id: 'directory-complete',
        title: 'Directory ready to explore',
        description: 'You can now browse your full team. Click any employee to see their profile — including skills, rates, and which projects they are currently allocated to.',
        placement: 'center',
      },
    ],
  },

  // ───────────────────────────────────────────────────────────────────────────
  // ROLES & RATES
  // ───────────────────────────────────────────────────────────────────────────
  {
    id: 'roles-rates-overview',
    name: 'Roles and Rates Tour',
    description: 'Understand how to view and manage role-based bill rates and cost rates used across project budgets.',
    trigger: 'first_login',
    priority: 65,
    isOptional: true,
    estimatedDuration: 60,
    pages: ['/roles-rates'],
    segment: ['admin', 'manager'],
    steps: [
      {
        id: 'roles-rates-welcome',
        title: 'Roles and Rates',
        description: 'This page manages the bill rates and cost rates assigned to each role. These rates flow through to project budgets, WIP calculations, and profitability reporting.',
        placement: 'center',
      },
      {
        id: 'roles-rates-search',
        title: 'Search and filter',
        description: 'Use the search box to find people by name or role. The department and role filters help narrow the list when managing a large team.',
        target: '[data-tour="roles-rates-search"]',
        placement: 'bottom',
        spotlightPadding: 10,
      },
      {
        id: 'roles-rates-table',
        title: 'Rate details',
        description: 'Each row shows a person\'s current bill rate per day and per hour, and their cost rate. Click a row to open the full profile where rates can be edited by authorised users.',
        target: '[data-tour="roles-rates-table"]',
        placement: 'top',
        spotlightPadding: 8,
      },
      {
        id: 'roles-rates-complete',
        title: 'Rates ready',
        description: 'Role-based rates are central to accurate project budgeting. Keep them up to date to ensure WIP values, budget forecasts, and profitability reports reflect your actual cost base.',
        placement: 'center',
      },
    ],
  },

  // ───────────────────────────────────────────────────────────────────────────
  // SKILLS
  // ───────────────────────────────────────────────────────────────────────────
  {
    id: 'skills-overview',
    name: 'Skills Directory Tour',
    description: 'Learn how to browse the skills catalogue, see which team members hold each skill, and use skills to find the right person for a project.',
    trigger: 'first_login',
    priority: 60,
    isOptional: true,
    estimatedDuration: 50,
    pages: ['/skills'],
    steps: [
      {
        id: 'skills-welcome',
        title: 'Skills Directory',
        description: 'The Skills page gives you a catalogue of all the capabilities across your team. You can see which skills exist, how many people hold each one, and drill in to see who has what.',
        placement: 'center',
      },
      {
        id: 'skills-search',
        title: 'Search and filter skills',
        description: 'Use the search box to find a specific skill by name or category. The category dropdown lets you filter by skill type — technical, leadership, industry, and more.',
        target: '[data-tour="skills-search"]',
        placement: 'bottom',
        spotlightPadding: 10,
      },
      {
        id: 'skills-table',
        title: 'Skills list',
        description: 'Each row shows the skill name, category, and how many team members hold it. Click any skill to open a detail view showing all the people who have that skill and their proficiency level.',
        target: '[data-tour="skills-table"]',
        placement: 'top',
        spotlightPadding: 8,
      },
      {
        id: 'skills-complete',
        title: 'Skills ready to explore',
        description: 'Use the Skills page when staffing projects to find team members with the right expertise. Individual employee profiles (via the Directory) also show skills with proficiency ratings.',
        placement: 'center',
      },
    ],
  },

  // ───────────────────────────────────────────────────────────────────────────
  // WIP REPORTING
  // ───────────────────────────────────────────────────────────────────────────
  {
    id: 'wip-reporting-overview',
    name: 'WIP Report Tour',
    description: 'Learn how to read the WIP report, understand unbilled exposure by project, and use filters to identify billing actions.',
    trigger: 'first_login',
    priority: 85,
    isOptional: true,
    estimatedDuration: 70,
    pages: ['/reports/wip'],
    segment: ['admin', 'manager'],
    steps: [
      {
        id: 'wip-welcome',
        title: 'WIP Reporting',
        description: 'Work in Progress (WIP) represents the value of work completed but not yet invoiced. This report helps you monitor unbilled exposure across all projects and identify when billing is overdue.',
        placement: 'center',
      },
      {
        id: 'wip-summary-cards',
        title: 'WIP summary metrics',
        description: 'The summary cards show total WIP value, WIP above threshold (requiring action), projects with positive WIP, and average days WIP has been outstanding.',
        target: '[data-tour="wip-summary-cards"]',
        placement: 'bottom',
        spotlightPadding: 10,
      },
      {
        id: 'wip-filters',
        title: 'Filters and date range',
        description: 'Use the project type filter and date range picker to narrow results. You can filter to T&M, Fixed Fee, or Retainer projects and select the reporting period.',
        target: '[data-tour="wip-filters"]',
        placement: 'bottom',
        spotlightPadding: 10,
      },
      {
        id: 'wip-table',
        title: 'WIP details table',
        description: 'Each row shows a project\'s total WIP, the breakdown by category, days outstanding, and the status of any billing actions. Click a row to navigate to the project\'s billing tab.',
        target: '[data-tour="wip-table"]',
        placement: 'top',
        spotlightPadding: 8,
      },
      {
        id: 'wip-complete',
        title: 'WIP report ready',
        description: 'Review this report weekly to stay on top of your billing cycle. High WIP with long outstanding days is a cash-flow risk — use this report to drive timely invoicing.',
        placement: 'center',
      },
    ],
  },

  // ───────────────────────────────────────────────────────────────────────────
  // PROJECT STATUS REPORT
  // ───────────────────────────────────────────────────────────────────────────
  {
    id: 'project-status-report-overview',
    name: 'Project Status Report Tour',
    description: 'Understand the project status report — health RAG ratings, completion trends, and how to use it for delivery governance.',
    trigger: 'first_login',
    priority: 80,
    isOptional: true,
    estimatedDuration: 60,
    pages: ['/reports/project-status'],
    steps: [
      {
        id: 'project-status-welcome',
        title: 'Project Status Report',
        description: 'This report gives you a RAG (Red, Amber, Green) view of all projects. It is the primary governance tool for tracking delivery health across your portfolio.',
        placement: 'center',
      },
      {
        id: 'project-status-filters',
        title: 'Report filters',
        description: 'Filter the report by health status, project type, date range, or project manager. Combine filters to focus on the subset of projects most relevant to your review.',
        target: '[data-tour="project-status-filters"]',
        placement: 'bottom',
        spotlightPadding: 10,
      },
      {
        id: 'project-status-table',
        title: 'Status report table',
        description: 'Each row shows a project\'s health indicator, completion percentage, budget position, milestone status, and last update. Red and amber rows should be reviewed and actioned.',
        target: '[data-tour="project-status-table"]',
        placement: 'top',
        spotlightPadding: 8,
      },
      {
        id: 'project-status-complete',
        title: 'Status report ready',
        description: 'Use this report in weekly governance meetings to keep your portfolio on track. Drill into red and amber projects directly from this view to understand root causes.',
        placement: 'center',
      },
    ],
  },

  // ───────────────────────────────────────────────────────────────────────────
  // BUDGET VARIANCE REPORT
  // ───────────────────────────────────────────────────────────────────────────
  {
    id: 'budget-variance-overview',
    name: 'Budget Variance Report Tour',
    description: 'Learn how to read budget variance data, identify over-spending projects, and drill into the underlying cause.',
    trigger: 'first_login',
    priority: 78,
    isOptional: true,
    estimatedDuration: 60,
    pages: ['/reports/budget-variance'],
    segment: ['admin', 'manager'],
    steps: [
      {
        id: 'budget-variance-welcome',
        title: 'Budget Variance Report',
        description: 'This report compares actual spend against the approved budget for every project. It helps you identify over-runs early and take corrective action before costs spiral.',
        placement: 'center',
      },
      {
        id: 'budget-variance-filters',
        title: 'Filter and focus',
        description: 'Use the filters to narrow by date range, project type, or project manager. You can also filter to show only over-budget or under-budget projects.',
        target: '[data-tour="budget-variance-filters"]',
        placement: 'bottom',
        spotlightPadding: 10,
      },
      {
        id: 'budget-variance-table',
        title: 'Variance breakdown',
        description: 'Each row shows the budgeted amount, actual spend, and the variance in both currency and percentage. Red rows indicate over-runs. Click a project to navigate directly to its budget.',
        target: '[data-tour="budget-variance-table"]',
        placement: 'top',
        spotlightPadding: 8,
      },
      {
        id: 'budget-variance-complete',
        title: 'Budget variance ready',
        description: 'Review this report monthly as part of your financial governance cycle. Early identification of variances gives you time to re-forecast, adjust scope, or escalate as needed.',
        placement: 'center',
      },
    ],
  },

  // ───────────────────────────────────────────────────────────────────────────
  // PROJECT P&L REPORT
  // ───────────────────────────────────────────────────────────────────────────
  {
    id: 'project-pl-overview',
    name: 'Project P&L Report Tour',
    description: 'Understand how to read the project profit and loss report and use it to assess financial performance by project.',
    trigger: 'first_login',
    priority: 76,
    isOptional: true,
    estimatedDuration: 60,
    pages: ['/reports/project-pl'],
    segment: ['admin', 'manager'],
    steps: [
      {
        id: 'project-pl-welcome',
        title: 'Project P&L Report',
        description: 'This report shows profit and loss per project — revenue, direct costs, gross profit, and margin percentage. It is the key financial performance view for your portfolio.',
        placement: 'center',
      },
      {
        id: 'project-pl-filters',
        title: 'Filter the report',
        description: 'Select a date range and filter by project type, status, or manager to focus on the data most relevant to your review.',
        target: '[data-tour="project-pl-filters"]',
        placement: 'bottom',
        spotlightPadding: 10,
      },
      {
        id: 'project-pl-table',
        title: 'P&L breakdown per project',
        description: 'Each row shows revenue, cost, gross profit, and margin % for a project. Green margin rows are meeting target; amber or red rows need attention. Click to drill into the project.',
        target: '[data-tour="project-pl-table"]',
        placement: 'top',
        spotlightPadding: 8,
      },
      {
        id: 'project-pl-complete',
        title: 'P&L report ready',
        description: 'Use this report for monthly financial reviews. It gives you clear visibility of which projects are profitable, which are at risk, and where action is needed.',
        placement: 'center',
      },
    ],
  },

  // ───────────────────────────────────────────────────────────────────────────
  // PROJECT FORECAST REPORT
  // ───────────────────────────────────────────────────────────────────────────
  {
    id: 'project-forecast-overview',
    name: 'Project Forecast Report Tour',
    description: 'Learn how to use the forecast report to compare projected completion against budget and timeline.',
    trigger: 'first_login',
    priority: 74,
    isOptional: true,
    estimatedDuration: 60,
    pages: ['/reports/project-forecast'],
    segment: ['admin', 'manager'],
    steps: [
      {
        id: 'project-forecast-welcome',
        title: 'Project Forecast Report',
        description: 'The Forecast report shows projected completion values for each project — comparing the original budget to the latest forecast. Use it to spot projects trending over or under.',
        placement: 'center',
      },
      {
        id: 'project-forecast-filters',
        title: 'Report filters',
        description: 'Filter by date range, project type, or project manager. You can also switch between seeing all projects or only those with significant forecast changes.',
        target: '[data-tour="project-forecast-filters"]',
        placement: 'bottom',
        spotlightPadding: 10,
      },
      {
        id: 'project-forecast-table',
        title: 'Forecast versus budget',
        description: 'Each row compares the original budget to the current forecast, showing the forecast variance. Projects where forecast exceeds budget by more than the tolerance threshold are highlighted.',
        target: '[data-tour="project-forecast-table"]',
        placement: 'top',
        spotlightPadding: 8,
      },
      {
        id: 'project-forecast-complete',
        title: 'Forecast report ready',
        description: 'Review forecasts regularly and update them in each project\'s details page. Accurate forecasting helps the business plan resources and cash flow with confidence.',
        placement: 'center',
      },
    ],
  },

  // ───────────────────────────────────────────────────────────────────────────
  // PROFITABILITY REPORT
  // ───────────────────────────────────────────────────────────────────────────
  {
    id: 'profitability-overview',
    name: 'Profitability Report Tour',
    description: 'Understand how to use the profitability report to analyse margins by client, project type, and team.',
    trigger: 'first_login',
    priority: 72,
    isOptional: true,
    estimatedDuration: 60,
    pages: ['/reports/profitability'],
    segment: ['admin', 'manager'],
    steps: [
      {
        id: 'profitability-welcome',
        title: 'Profitability Report',
        description: 'This report analyses gross margin across your portfolio — segmented by client, project type, and other dimensions. It is the key tool for understanding which parts of the business are most profitable.',
        placement: 'center',
      },
      {
        id: 'profitability-filters',
        title: 'Segment and filter',
        description: 'Use the filters to segment by date range, client, project type, or manager. The group-by control lets you pivot the view to compare different dimensions.',
        target: '[data-tour="profitability-filters"]',
        placement: 'bottom',
        spotlightPadding: 10,
      },
      {
        id: 'profitability-table',
        title: 'Margin breakdown',
        description: 'Each row shows revenue, direct costs, gross profit, and margin percentage. Rows are colour-coded against your target margin — making it easy to spot underperforming segments.',
        target: '[data-tour="profitability-table"]',
        placement: 'top',
        spotlightPadding: 8,
      },
      {
        id: 'profitability-complete',
        title: 'Profitability report ready',
        description: 'Use this report for quarterly business reviews to understand which clients and project types drive the most value. It supports pricing decisions and capacity planning.',
        placement: 'center',
      },
    ],
  },

  // ───────────────────────────────────────────────────────────────────────────
  // AUDIT LOG
  // ───────────────────────────────────────────────────────────────────────────
  {
    id: 'audit-log-overview',
    name: 'Audit Log Tour',
    description: 'Learn how to use the audit log to trace changes across projects, settings, budgets, and resources.',
    trigger: 'first_login',
    priority: 60,
    isOptional: true,
    estimatedDuration: 50,
    pages: ['/audit-log'],
    segment: ['admin'],
    steps: [
      {
        id: 'audit-log-welcome',
        title: 'Audit Log',
        description: 'The Audit Log records every significant action taken in the system — project changes, budget edits, settings updates, and more. It gives you a full traceable history for compliance and governance.',
        placement: 'center',
      },
      {
        id: 'audit-log-filters',
        title: 'Filter by category and user',
        description: 'Use the category dropdown to filter by action type — Project, Budget, Settings, Resource, or Client. You can also search for a specific user or date range.',
        target: '[data-tour="audit-log-filters"]',
        placement: 'bottom',
        spotlightPadding: 10,
      },
      {
        id: 'audit-log-table',
        title: 'Audit entries',
        description: 'Each row shows who made a change, what was changed, the category, and the timestamp. Click any row to expand it and see the full before-and-after detail of the change.',
        target: '[data-tour="audit-log-table"]',
        placement: 'top',
        spotlightPadding: 8,
      },
      {
        id: 'audit-log-export',
        title: 'Export the log',
        description: 'Use the Export button to download the current filtered view as an Excel file. This is useful for compliance audits and external reporting.',
        target: '[data-tour="audit-log-export"]',
        placement: 'left',
        spotlightPadding: 12,
      },
      {
        id: 'audit-log-complete',
        title: 'Audit log ready',
        description: 'You now know how to search, filter, and export the audit log. Use it to investigate unexpected changes, support compliance reviews, or answer governance queries.',
        placement: 'center',
      },
    ],
  },

  // ───────────────────────────────────────────────────────────────────────────
  // PROJECT TRANSACTIONS
  // ───────────────────────────────────────────────────────────────────────────
  {
    id: 'project-transactions-overview',
    name: 'Project Transactions Tour',
    description: 'Understand how to view, filter, and export the cross-project transaction log.',
    trigger: 'first_login',
    priority: 65,
    isOptional: true,
    estimatedDuration: 50,
    pages: ['/project-transactions'],
    segment: ['admin', 'manager'],
    steps: [
      {
        id: 'project-transactions-welcome',
        title: 'Project Transactions',
        description: 'This page shows a unified view of all financial transactions across every project — timesheets, expenses, purchase orders, and invoiced amounts in one place.',
        placement: 'center',
      },
      {
        id: 'project-transactions-filters',
        title: 'Filter transactions',
        description: 'Filter by date range, project, transaction type, or status. Multiple filters can be combined to narrow down exactly the transactions you need to review.',
        target: '[data-tour="transactions-filters"]',
        placement: 'bottom',
        spotlightPadding: 10,
      },
      {
        id: 'project-transactions-table',
        title: 'Transaction details',
        description: 'Each row shows the transaction date, project, type, amount, and status. The table supports sorting and column configuration so you can view the data in the most useful way.',
        target: '[data-tour="transactions-table"]',
        placement: 'top',
        spotlightPadding: 8,
      },
      {
        id: 'project-transactions-complete',
        title: 'Transactions ready',
        description: 'Use this page for financial reconciliation, cross-project cost reviews, and pre-billing checks. The export function lets you download the filtered dataset for external analysis.',
        placement: 'center',
      },
    ],
  },

  // ───────────────────────────────────────────────────────────────────────────
  // PROJECT TEMPLATES
  // ───────────────────────────────────────────────────────────────────────────
  {
    id: 'project-templates-overview',
    name: 'Project Templates Tour',
    description: 'Learn how to create, manage, and use project templates to speed up project setup.',
    trigger: 'first_login',
    priority: 65,
    isOptional: true,
    estimatedDuration: 60,
    pages: ['/project-templates'],
    segment: ['admin', 'manager'],
    steps: [
      {
        id: 'project-templates-welcome',
        title: 'Project Templates',
        description: 'Templates let you define a reusable project structure — including tasks, milestones, team roles, and budget categories — so new projects can be set up consistently and quickly.',
        placement: 'center',
      },
      {
        id: 'project-templates-list',
        title: 'Template library',
        description: 'All available templates are listed here. Each card shows the template name, type, and a summary of what is included. You can create, edit, duplicate, or delete templates.',
        target: '[data-tour="project-templates-list"]',
        placement: 'bottom',
        spotlightPadding: 10,
      },
      {
        id: 'project-templates-create',
        title: 'Creating a new template',
        description: 'Click the New Template button to define a template from scratch, or save an existing project as a template directly from the Projects list using the Save as Template action.',
        target: '[data-tour="project-templates-create-btn"]',
        placement: 'left',
        spotlightPadding: 12,
      },
      {
        id: 'project-templates-complete',
        title: 'Templates ready',
        description: 'Well-designed templates reduce setup time and ensure consistency across projects. They are particularly valuable for recurring project types such as fixed-fee engagements or retainer arrangements.',
        placement: 'center',
      },
    ],
  },

  // ───────────────────────────────────────────────────────────────────────────
  // SETTINGS HUB
  // ───────────────────────────────────────────────────────────────────────────
  {
    id: 'settings-hub-overview',
    name: 'Settings Hub Tour',
    description: 'Learn how to navigate the settings hub and understand which settings control what.',
    trigger: 'first_login',
    priority: 70,
    isOptional: true,
    estimatedDuration: 50,
    pages: ['/settings'],
    segment: ['admin'],
    steps: [
      {
        id: 'settings-hub-welcome',
        title: 'Settings Hub',
        description: 'The Settings Hub is the central configuration area for Access Projects. From here you can manage general settings, project defaults, integrations, user permissions, notifications, and more.',
        placement: 'center',
      },
      {
        id: 'settings-hub-cards',
        title: 'Settings categories',
        description: 'Each card represents a settings section. Click any card to navigate directly to that area. The cards show a brief description of what each section controls.',
        target: '[data-tour="settings-hub-cards"]',
        placement: 'top',
        spotlightPadding: 10,
      },
      {
        id: 'settings-hub-complete',
        title: 'Settings ready',
        description: 'Take a few minutes to explore each settings section. Getting the defaults right early — especially for project settings, billing, and permissions — saves time as the platform scales.',
        placement: 'center',
      },
    ],
  },

  // ───────────────────────────────────────────────────────────────────────────
  // SETTINGS — GENERAL
  // ───────────────────────────────────────────────────────────────────────────
  {
    id: 'settings-general-overview',
    name: 'General Settings Tour',
    description: 'Learn what the general settings control — company details, currency, date formats, and system-wide defaults.',
    trigger: 'first_login',
    priority: 68,
    isOptional: true,
    estimatedDuration: 50,
    pages: ['/settings/general'],
    segment: ['admin'],
    steps: [
      {
        id: 'settings-general-welcome',
        title: 'General Settings',
        description: 'General Settings control the company-level configuration — your organisation name, currency, fiscal year, date format, and other global defaults that apply across the whole system.',
        placement: 'center',
      },
      {
        id: 'settings-general-company',
        title: 'Company details',
        description: 'Set your company name, logo, and contact details here. These appear in reports, invoices, and export headers.',
        target: '[data-tour="settings-general-company"]',
        placement: 'bottom',
        spotlightPadding: 10,
      },
      {
        id: 'settings-general-currency',
        title: 'Currency and number formats',
        description: 'Set your base currency and number formatting preferences. These affect how financial values are displayed across all reports and project views.',
        target: '[data-tour="settings-general-currency"]',
        placement: 'top',
        spotlightPadding: 10,
      },
      {
        id: 'settings-general-complete',
        title: 'General settings ready',
        description: 'Make sure your company details and currency settings are correct before going live. These settings influence all financial calculations and exported documents.',
        placement: 'center',
      },
    ],
  },

  // ───────────────────────────────────────────────────────────────────────────
  // SETTINGS — PROJECT
  // ───────────────────────────────────────────────────────────────────────────
  {
    id: 'settings-project-overview',
    name: 'Project Settings Tour',
    description: 'Understand the project-level defaults — statuses, health thresholds, billing settings, and custom fields.',
    trigger: 'first_login',
    priority: 66,
    isOptional: true,
    estimatedDuration: 60,
    pages: ['/settings/project'],
    segment: ['admin'],
    steps: [
      {
        id: 'settings-project-welcome',
        title: 'Project Settings',
        description: 'Project Settings define how projects behave by default — from status workflows and health thresholds to billing configuration and custom fields. These are inherited by every project you create.',
        placement: 'center',
      },
      {
        id: 'settings-project-statuses',
        title: 'Project statuses',
        description: 'Define the lifecycle stages a project can pass through — for example Draft, Active, On Hold, and Completed. You can customise labels and control which statuses are available for selection.',
        target: '[data-tour="settings-project-statuses"]',
        placement: 'bottom',
        spotlightPadding: 10,
      },
      {
        id: 'settings-project-health',
        title: 'Health thresholds',
        description: 'Set the thresholds that determine when a project is flagged as amber or red. These drive the health indicators across the Portfolio, Dashboard, and Project Status Report.',
        target: '[data-tour="settings-project-health"]',
        placement: 'top',
        spotlightPadding: 10,
      },
      {
        id: 'settings-project-complete',
        title: 'Project settings ready',
        description: 'Well-configured project defaults reduce manual effort and ensure consistent governance. Revisit these settings as your delivery model evolves.',
        placement: 'center',
      },
    ],
  },

  // ───────────────────────────────────────────────────────────────────────────
  // SETTINGS — USER PERMISSIONS
  // ───────────────────────────────────────────────────────────────────────────
  {
    id: 'settings-permissions-overview',
    name: 'User Permissions Tour',
    description: 'Learn how role-based permissions work and how to configure access levels for different user types.',
    trigger: 'first_login',
    priority: 64,
    isOptional: true,
    estimatedDuration: 60,
    pages: ['/settings/user-permissions'],
    segment: ['admin'],
    steps: [
      {
        id: 'settings-permissions-welcome',
        title: 'User Permissions',
        description: 'Permissions in Access Projects are role-based. Each platform role defines what its members can see and do. This page shows all roles and their configured access levels.',
        placement: 'center',
      },
      {
        id: 'settings-permissions-roles-list',
        title: 'Role list',
        description: 'Each card represents a platform role — such as Admin, Manager, or Employee. The card shows a summary of the key permissions assigned to that role.',
        target: '[data-tour="settings-permissions-roles-list"]',
        placement: 'bottom',
        spotlightPadding: 10,
      },
      {
        id: 'settings-permissions-edit',
        title: 'Editing a role',
        description: 'Click a role card to open the full permissions editor. You can toggle individual permissions on or off for each feature area — data access, reports, settings, and project actions.',
        target: '[data-tour="settings-permissions-edit-btn"]',
        placement: 'left',
        spotlightPadding: 12,
      },
      {
        id: 'settings-permissions-complete',
        title: 'Permissions ready',
        description: 'Getting permissions right is essential for data security and clean UX. Users only see what their role allows — reducing confusion and protecting sensitive financial data.',
        placement: 'center',
      },
    ],
  },

  // ───────────────────────────────────────────────────────────────────────────
  // SETTINGS — INTEGRATIONS
  // ───────────────────────────────────────────────────────────────────────────
  {
    id: 'settings-integrations-overview',
    name: 'Integrations Tour',
    description: 'Learn how to connect Access Projects to your accounting, HR, and time-tracking systems.',
    trigger: 'first_login',
    priority: 62,
    isOptional: true,
    estimatedDuration: 60,
    pages: ['/settings/integrations'],
    segment: ['admin'],
    steps: [
      {
        id: 'settings-integrations-welcome',
        title: 'Integrations',
        description: 'Access Projects can connect to external systems — accounting platforms, HR systems, and time-tracking tools — to sync data automatically and reduce manual entry.',
        placement: 'center',
      },
      {
        id: 'settings-integrations-list',
        title: 'Available integrations',
        description: 'Each card represents an available integration. The status indicator shows whether it is active, disconnected, or not yet configured. Click a card to configure or manage that connection.',
        target: '[data-tour="settings-integrations-list"]',
        placement: 'bottom',
        spotlightPadding: 10,
      },
      {
        id: 'settings-integrations-mapping',
        title: 'Field mapping',
        description: 'Once connected, each integration includes a mapping configuration where you align your external system\'s data fields to Access Projects fields — ensuring data flows correctly.',
        target: '[data-tour="settings-integrations-mapping"]',
        placement: 'top',
        spotlightPadding: 10,
      },
      {
        id: 'settings-integrations-complete',
        title: 'Integrations ready',
        description: 'Well-configured integrations significantly reduce manual data entry and improve accuracy. Start with your accounting platform to enable live financial reporting.',
        placement: 'center',
      },
    ],
  },

  // ───────────────────────────────────────────────────────────────────────────
  // SETTINGS — NOTIFICATIONS
  // ───────────────────────────────────────────────────────────────────────────
  {
    id: 'settings-notifications-overview',
    name: 'Notifications Tour',
    description: 'Learn how to configure system and email notifications for project events, budget alerts, and milestone reminders.',
    trigger: 'first_login',
    priority: 58,
    isOptional: true,
    estimatedDuration: 50,
    pages: ['/settings/notifications'],
    segment: ['admin'],
    steps: [
      {
        id: 'settings-notifications-welcome',
        title: 'Notification Settings',
        description: 'Notifications keep your team informed about project events — budget thresholds crossed, milestones due, status changes, and more. This page controls which notifications are active and how they are sent.',
        placement: 'center',
      },
      {
        id: 'settings-notifications-triggers',
        title: 'Notification triggers',
        description: 'Each notification type has a toggle to enable or disable it. Scroll through the list to review all available triggers — from budget over-run alerts to milestone deadline reminders.',
        target: '[data-tour="settings-notifications-triggers"]',
        placement: 'bottom',
        spotlightPadding: 10,
      },
      {
        id: 'settings-notifications-complete',
        title: 'Notifications configured',
        description: 'Proactive notifications reduce the need for manual monitoring. Enable the ones that match your team\'s governance rhythm and adjust as your processes mature.',
        placement: 'center',
      },
    ],
  },

  // ───────────────────────────────────────────────────────────────────────────
  // SETTINGS — USER PREFERENCES
  // ───────────────────────────────────────────────────────────────────────────
  {
    id: 'settings-user-preferences-overview',
    name: 'User Preferences Tour',
    description: 'Personalise your Access Projects experience — themes, date formats, default views, and notification preferences.',
    trigger: 'first_login',
    priority: 56,
    isOptional: true,
    estimatedDuration: 50,
    pages: ['/settings/user-preferences'],
    steps: [
      {
        id: 'settings-user-preferences-welcome',
        title: 'User Preferences',
        description: 'User Preferences are personal settings that apply only to your account. You can customise your theme, default page views, number formats, and notification preferences here.',
        placement: 'center',
      },
      {
        id: 'settings-user-preferences-display',
        title: 'Display preferences',
        description: 'Set your preferred theme (light or dark), date format, and number display style. These settings are saved to your profile and persist across devices.',
        target: '[data-tour="settings-user-preferences-display"]',
        placement: 'bottom',
        spotlightPadding: 10,
      },
      {
        id: 'settings-user-preferences-complete',
        title: 'Preferences saved',
        description: 'Your preferences are applied immediately and stored to your user profile. You can update them at any time by returning to this page.',
        placement: 'center',
      },
    ],
  },

  // ───────────────────────────────────────────────────────────────────────────
  // SETTINGS — TEMPLATES
  // ───────────────────────────────────────────────────────────────────────────
  {
    id: 'settings-templates-overview',
    name: 'Template Settings Tour',
    description: 'Learn how to manage email templates, invoice templates, and document templates used across the system.',
    trigger: 'first_login',
    priority: 58,
    isOptional: true,
    estimatedDuration: 50,
    pages: ['/settings/templates'],
    segment: ['admin'],
    steps: [
      {
        id: 'settings-templates-welcome',
        title: 'Template Settings',
        description: 'This section manages the document and email templates used across Access Projects — from invoice layouts and billing emails to status report formats.',
        placement: 'center',
      },
      {
        id: 'settings-templates-list',
        title: 'Template library',
        description: 'All configured templates are listed here. Each entry shows the template name, type, and whether it is set as the default. Click a template to edit its content and layout.',
        target: '[data-tour="settings-templates-list"]',
        placement: 'bottom',
        spotlightPadding: 10,
      },
      {
        id: 'settings-templates-complete',
        title: 'Templates ready',
        description: 'Well-crafted templates give your documents a professional appearance and ensure consistency. Start with the invoice template as this is the most client-facing output.',
        placement: 'center',
      },
    ],
  },

];

// ── Helper functions ──────────────────────────────────────────────────────────

export function getTourById(tourId: string): Tour | undefined {
  return tours.find(t => t.id === tourId);
}

export function getToursBySegment(segment: UserSegment): Tour[] {
  return tours
    .filter(t => !t.segment || t.segment.includes(segment))
    .sort((a, b) => b.priority - a.priority);
}

export function getToursByTrigger(trigger: Tour['trigger']): Tour[] {
  return tours.filter(t => t.trigger === trigger);
}

/**
 * Returns tours relevant to the given route path.
 *
 * Matching rules:
 *  - Tours with no `pages` array are global (shown everywhere)
 *  - Tours with `pages: ['*']` are global
 *  - Tours with `pages` containing the path are included
 *    (prefix match so '/projects/' matches '/projects/123')
 *
 * If both the tour and the caller supply a segment, the tour must include
 * that segment. Tours with no segment array are shown to everyone.
 */
export function getToursByPage(path: string, userSegment?: UserSegment): Tour[] {
  return tours
    .filter(t => {
      // Page filter
      const pageMatch =
        !t.pages ||
        t.pages.includes('*') ||
        t.pages.some(p => path === p || path.startsWith(p + '/') || (p.endsWith('/') && path.startsWith(p)));

      if (!pageMatch) return false;

      // Segment filter (only applies when BOTH sides define a segment)
      if (userSegment && t.segment && t.segment.length > 0) {
        return t.segment.includes(userSegment);
      }

      return true;
    })
    .sort((a, b) => b.priority - a.priority);
}
